import { css } from 'lit';

export const sharedUiStyles = css`
  :host {
    --kv-surface: var(--color-surface, var(--color-sidebar-surface, #fff));
    --kv-surface-muted: var(
      --color-surface-muted,
      var(--color-sidebar-bg, #f8fafc)
    );
    --kv-surface-hover: var(
      --color-surface-hover,
      var(--color-sidebar-surface-hover, #f1f5f9)
    );
    --kv-border: var(--color-app-border, rgba(15, 23, 42, 0.1));
    --kv-accent: var(--color-accent, var(--color-sidebar-selected-border));
    --kv-accent-bg: var(--color-accent-bg, var(--color-sidebar-selected-bg));
    --kv-accent-border: var(--color-accent-border, rgba(63, 99, 243, 0.28));
    --kv-accent-border-subtle: var(
      --color-accent-border-subtle,
      rgba(63, 99, 243, 0.18)
    );
    --kv-control-bg: var(--color-control-bg, var(--kv-accent-bg));
    --kv-control-hover: var(--color-control-hover, var(--kv-surface-hover));
    --kv-control-active: var(--color-control-active, var(--kv-accent-bg));
    --kv-control-border: var(
      --color-control-border,
      rgba(63, 99, 243, 0.2)
    );
    --kv-danger: var(--color-danger, var(--color-error-text, #b91c1c));
    --kv-danger-bg: var(--color-danger-bg, rgba(220, 38, 38, 0.08));
    --kv-danger-hover: var(--color-danger-hover, rgba(220, 38, 38, 0.14));
    --kv-danger-active: var(--color-danger-active, rgba(220, 38, 38, 0.2));
    --kv-danger-border: var(
      --color-danger-border,
      rgba(220, 38, 38, 0.18)
    );
    --kv-warning-bg: var(--color-warning-bg, rgba(245, 158, 11, 0.14));
    --kv-warning-text: var(--color-warning-text, #9a5b00);
    --kv-shadow: var(--elevation-panel, 0 10px 24px rgba(15, 23, 42, 0.06));
    --kv-card-shadow: var(--elevation-card, 0 1px 2px rgba(15, 23, 42, 0.04));
    --kv-radius-panel: var(--radius-panel, 8px);
    --kv-radius-control: var(--radius-control, 8px);
    --kv-radius-chip: var(--radius-chip, 999px);
  }

  .kv-panel,
  .kv-card,
  .kv-table-shell,
  .kv-state-panel {
    background: var(--kv-surface);
    border: 1px solid var(--kv-border);
    border-radius: var(--kv-radius-panel);
    box-sizing: border-box;
    min-width: 0;
  }

  .kv-panel,
  .kv-table-shell,
  .kv-state-panel {
    box-shadow: var(--kv-shadow);
  }

  .kv-card {
    box-shadow: var(--kv-card-shadow);
  }

  .kv-action-card {
    appearance: none;
    background: var(--kv-surface);
    border: 1px dashed var(--kv-accent-border);
    border-radius: var(--kv-radius-panel);
    box-sizing: border-box;
    color: var(--color-app-text);
    cursor: pointer;
    font: inherit;
    min-width: 0;
    text-align: left;
    transition:
      background-color 0.14s ease,
      border-color 0.14s ease,
      box-shadow 0.14s ease,
      transform 0.14s ease;
  }

  .kv-action-card:hover {
    background: var(--kv-accent-bg);
    border-color: var(--kv-accent);
    box-shadow: var(--kv-shadow);
    transform: translateY(-1px);
  }

  .kv-action-card:focus-visible {
    outline: 2px solid var(--kv-accent);
    outline-offset: 2px;
  }

  .kv-chip {
    align-items: center;
    border-radius: var(--kv-radius-chip);
    display: inline-flex;
    font-size: 11px;
    font-weight: 700;
    gap: 5px;
    line-height: 1.2;
    padding: 4px 9px;
    white-space: nowrap;
  }

  .kv-chip.success {
    background: var(--color-success-bg);
    color: var(--color-success-text);
  }

  .kv-chip.error {
    background: var(--color-error-bg);
    color: var(--color-error-text);
  }

  .kv-chip.warning {
    background: var(--kv-warning-bg);
    color: var(--kv-warning-text);
  }

  .kv-chip.neutral {
    background: var(--kv-surface-muted);
    color: var(--color-app-text-secondary);
  }

  .kv-message {
    align-items: center;
    border-radius: var(--kv-radius-panel);
    display: flex;
    font-size: 13px;
    gap: 8px;
    line-height: 1.35;
    padding: 12px 14px;
  }

  .kv-message.success {
    background: var(--color-success-bg);
    color: var(--color-success-text);
  }

  .kv-message.error {
    background: var(--color-error-bg);
    color: var(--color-error-text);
  }

  .kv-code-pane {
    background: var(--kv-surface);
    border: 1px solid var(--kv-border);
    border-radius: var(--kv-radius-panel);
    color: var(--color-app-text);
    font-family: var(--mono, ui-monospace, SFMono-Regular, Menlo, monospace);
    font-size: 12px;
    line-height: 1.5;
    overflow: auto;
    scrollbar-width: thin;
  }

  sp-textfield,
  sp-picker {
    width: 100%;
  }

  sp-textfield {
    --mod-textfield-background-color: var(--kv-surface);
    --spectrum-textfield-background-color: var(--kv-surface);
    --mod-textfield-border-color: var(--kv-border);
    --mod-textfield-focus-indicator-color: var(--kv-accent);
  }

  sp-picker {
    cursor: pointer;
    --mod-picker-background-color-default: var(--kv-control-bg);
    --mod-picker-background-color-default-open: var(--kv-control-bg);
    --mod-picker-background-color-hover: var(--kv-control-hover);
    --mod-picker-background-color-hover-open: var(--kv-control-hover);
    --mod-picker-background-color-active: var(--kv-control-active);
    --mod-picker-background-color-key-focus: var(--kv-control-bg);
    --mod-picker-border-color-default: var(--kv-control-border);
    --mod-picker-border-default-open: var(--kv-accent-border);
    --mod-picker-border-color-default-open: var(--kv-accent-border);
    --mod-picker-border-color-hover: var(--kv-accent-border);
    --mod-picker-border-color-hover-open: var(--kv-accent-border);
    --mod-picker-border-active: var(--color-accent-border-strong);
    --mod-picker-border-color-active: var(--color-accent-border-strong);
    --mod-picker-border-color-key-focus: var(--kv-accent);
    --mod-picker-focus-indicator-color: var(--kv-accent);
    --mod-picker-font-color-default: var(--color-app-text);
    --mod-picker-font-color-default-open: var(--color-app-text);
    --mod-picker-font-color-hover: var(--color-app-text);
    --mod-picker-font-color-hover-open: var(--color-app-text);
    --mod-picker-font-color-active: var(--color-app-text);
    --mod-picker-font-color-key-focus: var(--color-app-text);
    --mod-picker-icon-color-default: var(--kv-accent);
    --mod-picker-icon-color-default-open: var(--kv-accent);
    --mod-picker-icon-color-hover: var(--kv-accent);
    --mod-picker-icon-color-hover-open: var(--kv-accent);
    --mod-picker-icon-color-active: var(--kv-accent);
    --mod-picker-icon-color-key-focus: var(--kv-accent);
  }

  sp-menu-item {
    cursor: pointer;
  }

  sp-button[variant='secondary']:not(.kv-danger-button):not(
      .settings-danger-button
    ):not(.danger-button) {
    --mod-button-background-color-default: var(--kv-control-bg);
    --mod-button-background-color-hover: var(--kv-control-hover);
    --mod-button-background-color-down: var(--kv-control-active);
    --mod-button-background-color-focus: var(--kv-control-bg);
    --mod-button-border-color-default: var(--kv-control-border);
    --mod-button-border-color-hover: var(--kv-accent-border);
    --mod-button-border-color-down: var(--color-accent-border-strong);
    --mod-button-border-color-focus: var(--kv-accent);
    --mod-button-content-color-default: var(--kv-accent);
    --mod-button-content-color-hover: var(--kv-accent);
    --mod-button-content-color-down: var(--kv-accent);
    --mod-button-content-color-focus: var(--kv-accent);
    --mod-button-focus-ring-color: var(--kv-accent);
  }

  sp-button.kv-danger-button,
  sp-button.settings-danger-button,
  sp-button.danger-button {
    --mod-button-background-color-default: var(--kv-danger-bg);
    --mod-button-background-color-hover: var(--kv-danger-hover);
    --mod-button-background-color-down: var(--kv-danger-active);
    --mod-button-background-color-focus: var(--kv-danger-bg);
    --mod-button-border-color-default: var(--kv-danger-border);
    --mod-button-border-color-hover: color-mix(
      in srgb,
      var(--kv-danger-border) 70%,
      var(--kv-danger)
    );
    --mod-button-border-color-down: color-mix(
      in srgb,
      var(--kv-danger-border) 55%,
      var(--kv-danger)
    );
    --mod-button-border-color-focus: var(--kv-danger);
    --mod-button-content-color-default: var(--kv-danger);
    --mod-button-content-color-hover: var(--kv-danger);
    --mod-button-content-color-down: var(--kv-danger);
    --mod-button-content-color-focus: var(--kv-danger);
    --mod-button-focus-ring-color: var(--kv-danger);
  }
`;
